// Add Image guide — the AI-powered image modal (AddImageModal: AI Generate / library /
// upload tabs + the Activity Log toggle in the header).
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const addImageGuide = {
  id: "add-image",
  icon: "image",
  keywords: [
    "add image",
    "görsel ekle",
    "ai image",
    "yapay zeka görsel",
    "generate image",
    "görsel oluştur",
    "image style",
    "görsel stili",
    "style reference",
    "stil referansı",
    "negative prompt",
    "negatif prompt",
    "enrich prompt",
    "promptu zenginleştir",
    "text in image",
    "resimde metin",
    "aspect ratio",
    "en boy oranı",
    "resolution",
    "çözünürlük",
    "ai provider",
    "ai sağlayıcısı",
    "nano banana",
    "gpt image",
    "flux",
    "aidea wizard ai",
    "upload image",
    "görsel yükle",
    "image library",
    "kütüphaneden seç",
    "activity log",
    "etkinlik günlüğü",
    "history",
    "geçmiş",
    "credits",
    "kredi",
    "agregar imagen",
    "imagen con ia",
    "generar imagen",
    "estilo",
    "referencia de estilo",
    "prompt negativo",
    "relación de aspecto",
    "resolución",
    "subir imagen",
    "biblioteca",
    "registro de actividad",
    "créditos",
  ],
  content: {
    en: {
      feature: "AI",
      title: "Add Image",
      sections: [
        {
          id: "overview",
          title: "Adding an image",
          videoUrl: null,
          text:
            "Click **Add Image** among the AI tools in the top bar (on a narrow screen they are grouped under one **AI** button) to put an image on the current scene. The modal has three tabs: **AI Generate**, **Choose from library** and **Upload my image**. The clock icon in the modal header opens the **Activity Log**.\n\n" +
            "- AI image generation spends **credits**; your balance is shown next to your name in the top bar.\n" +
            "- On the AI tab, the **Credits** button in the footer shows the estimated cost and your balance before you generate.\n" +
            "- Once the image is on the scene, you can keep working on it from the **AI Wizard** and **Tools** tabs of the right-side menu — each has its own guide.",
        },
        {
          id: "ai-generate",
          title: "AI Generate tab",
          videoUrl: null,
          text:
            "Pick a **Style**, describe the image, then click **Generate Image** in the footer.\n\n" +
            "- **Write Prompt** — describe the image yourself.\n" +
            "- **From Narration** — starts from the scene's narration; trim it to what should be shown, then click **Generate Image Prompt from Narration** to turn it into an image prompt.\n" +
            "- **Continue with Direct Prompt** — sends your text as it is, without a style or text overlays.\n" +
            "- **Enrich my prompt** rewrites what you typed into a more detailed prompt.\n" +
            "- **Style** — pick a style card (**More styles** shows the rest), upload a **Style Reference** image to copy its look, or choose **Custom** and describe your own style.\n" +
            "- **Optional Enhancements** — turn on **Text in Image** to place up to five texts in the image (add more with **+ Add Text**, each with its own position and style), and set the **Aspect Ratio** (16:9, 9:16, 1:1, 4:3) and **Resolution** (1K, 2K, 4K).\n" +
            "- **Advanced Settings** — a **Negative Prompt** lists what the image should not contain.",
        },
        {
          id: "library-upload",
          title: "Library and upload",
          videoUrl: null,
          text:
            "- **Choose from library** — browse or search the images already in your library; double-click an image, or hover over it and click its **+** button, to add it to the scene.\n" +
            "- **Upload my image** — drag and drop an image file or click to browse, then click **Upload file**. The image is added to the current scene as soon as the upload finishes; **Clear** lets you pick a different file. Images up to 100 MB are accepted.",
        },
        {
          id: "history",
          title: "Activity Log",
          videoUrl: null,
          text:
            "Click the clock icon in the modal header to open the **Activity Log**; click it again to return to the AI tab. It lists this training's recent AI image generations with their model, date, prompt and status, together with your **Current Balance** and **Image Spending**.\n\n" +
            "- **All Activities** widens the list to every kind of AI job.\n" +
            "- Double-click a row, or use its **+** button, to add a past result to the current scene **without spending credits again**.",
        },
        {
          id: "providers-results",
          title: "AI providers and results",
          videoUrl: null,
          text:
            "- **AI Provider** — **Auto** uses Nano Banana Pro. Switch to **Custom** to choose one or more of Nano Banana 2, Nano Banana Pro, Grok, Flux and GPT Image; every selected provider makes its own image, so you can compare them side by side. The **Credits** estimate then lists the cost per provider.\n" +
            "- **Resolution** can only be changed when every selected provider is a Nano Banana model.\n" +
            "- Each result offers **Add to Scene** and **Aidea Wizard AI**, which opens the AI editing tools (edit, remove background, upscale, animate and more) right inside the modal; **Change Tool** goes back to the tool list.\n" +
            "- **Regenerate** runs the same settings again. **Cancel** returns to the form and keeps your prompt and choices.\n" +
            "- Generated images also appear in the job tray, where they can be approved onto the scene later.",
        },
      ],
    },
    tr: {
      feature: "Yapay Zeka",
      title: "Görsel Ekle",
      sections: [
        {
          id: "overview",
          title: "Görsel ekleme",
          videoUrl: null,
          text:
            "Mevcut sahneye görsel eklemek için üst çubuktaki yapay zeka araçlarından **Görsel Ekle**'ye tıklayın (dar ekranda bu araçlar tek bir **AI** düğmesinin altında toplanır). Modalda üç sekme vardır: **AI İle Oluştur**, **Kütüphaneden Seç** ve **Görsel Yükle**. Modal başlığındaki saat simgesi **Etkinlik Günlüğü**'nü açar.\n\n" +
            "- Yapay zeka ile görsel oluşturmak **kredi** harcar; bakiyeniz üst çubukta adınızın yanında görünür.\n" +
            "- AI sekmesinde alt kısımdaki **Kredi** düğmesi, oluşturmadan önce tahmini maliyeti ve bakiyenizi gösterir.\n" +
            "- Görsel sahneye eklendikten sonra sağ menüdeki **AI Wizard** ve **Araçlar** sekmelerinden üzerinde çalışmaya devam edebilirsiniz; her birinin kendi rehberi vardır.",
        },
        {
          id: "ai-generate",
          title: "AI İle Oluştur sekmesi",
          videoUrl: null,
          text:
            "Bir **Stil** seçin, görseli tanımlayın, ardından alt kısımdaki **Görsel Oluştur**'a tıklayın.\n\n" +
            "- **Prompt Yaz** — görseli kendiniz tanımlayın.\n" +
            "- **Anlatımdan** — sahnenin anlatım metninden başlar; metni gösterilmesi gerekenlere göre kısaltın, ardından görsel prompt'una dönüştürmek için **Anlatımdan Görsel Prompt Oluştur**'a tıklayın.\n" +
            "- **Direkt Promptla İlerle** — metninizi stil ve metin bindirmesi uygulamadan olduğu gibi gönderir.\n" +
            "- **Promptu Zenginleştir**, yazdığınız metni daha ayrıntılı bir prompt'a dönüştürür.\n" +
            "- **Stil** — bir stil kartı seçin (**Daha Fazla Stil** diğerlerini gösterir), görünümünü örnek almak için bir **Stil Referansı** görseli yükleyin ya da **Özel**'i seçip kendi stilinizi tanımlayın.\n" +
            "- **Opsiyonel İyileştirmeler** — görsele en fazla beş metin yerleştirmek için **Resimde Metin**'i açın (**+ Metin Ekle** ile yenisini ekleyin; her birinin konumu ve stili ayrı seçilir), ardından **En Boy Oranı** (16:9, 9:16, 1:1, 4:3) ve **Çözünürlük** (1K, 2K, 4K) ayarlarını yapın.\n" +
            "- **Gelişmiş Ayarlar** — **Negatif Prompt** alanına görselde olmasını istemediğiniz şeyleri yazın.",
        },
        {
          id: "library-upload",
          title: "Kütüphane ve yükleme",
          videoUrl: null,
          text:
            "- **Kütüphaneden Seç** — kütüphanenizdeki görsellere göz atın veya arama yapın; sahneye eklemek için görsele çift tıklayın ya da üzerine gelip **+** düğmesine tıklayın.\n" +
            "- **Görsel Yükle** — bir görsel dosyasını sürükleyip bırakın veya tıklayıp seçin, ardından **Yükle**'ye tıklayın. Yükleme biter bitmez görsel mevcut sahneye eklenir; başka bir dosya seçmek için **Temizle**'yi kullanın. En fazla 100 MB'lık görseller kabul edilir.",
        },
        {
          id: "history",
          title: "Etkinlik Günlüğü",
          videoUrl: null,
          text:
            "**Etkinlik Günlüğü**'nü açmak için modal başlığındaki saat simgesine tıklayın; AI sekmesine dönmek için simgeye yeniden tıklayın. Günlük, bu eğitimdeki son yapay zeka görsel üretimlerini model, tarih, prompt ve durum bilgisiyle; **Mevcut Bakiye** ve **Görsel Harcaması** ile birlikte listeler.\n\n" +
            "- **Tüm Aktiviteler**, listeyi tüm yapay zeka iş türlerini kapsayacak şekilde genişletir.\n" +
            "- Geçmiş bir sonucu **yeniden kredi harcamadan** mevcut sahneye eklemek için satıra çift tıklayın veya satırdaki **+** düğmesini kullanın.",
        },
        {
          id: "providers-results",
          title: "AI sağlayıcıları ve sonuçlar",
          videoUrl: null,
          text:
            "- **AI Sağlayıcısı** — **Otomatik**, Nano Banana Pro'yu kullanır. **Özel**'e geçerek Nano Banana 2, Nano Banana Pro, Grok, Flux ve GPT Image arasından bir veya birkaçını seçebilirsiniz; seçilen her sağlayıcı kendi görselini üretir, böylece sonuçları yan yana karşılaştırabilirsiniz. Bu durumda **Kredi** tahmini maliyeti sağlayıcı bazında listeler.\n" +
            "- **Çözünürlük** yalnızca seçilen tüm sağlayıcılar Nano Banana modeliyse değiştirilebilir.\n" +
            "- Her sonuçta **Sahneye Ekle** ve **Aidea Wizard AI** düğmeleri bulunur; **Aidea Wizard AI**, yapay zeka düzenleme araçlarını (düzenleme, arka plan kaldırma, çözünürlük artırma, canlandırma ve daha fazlası) doğrudan modalın içinde açar. **Araç Değiştir** araç listesine geri döner.\n" +
            "- **Yeniden Oluştur** aynı ayarlarla tekrar üretir. **Vazgeç** forma geri döner; prompt'unuz ve seçimleriniz korunur.\n" +
            "- Üretilen görseller iş kuyruğunda (widget) da görünür; oradan daha sonra onaylanıp sahneye eklenebilir.",
        },
      ],
    },
    es: {
      feature: "IA",
      title: "Agregar imagen",
      sections: [
        {
          id: "overview",
          title: "Añadir una imagen",
          videoUrl: null,
          text:
            "Haz clic en **Agregar Imagen** entre las herramientas de IA de la barra superior (en pantallas estrechas se agrupan bajo un único botón **IA**) para poner una imagen en la escena actual. La ventana tiene tres pestañas: **Generar con IA**, **Elegir de biblioteca** y **Subir mi imagen**. El icono del reloj en el encabezado de la ventana abre el **Registro de Actividad**.\n\n" +
            "- La generación de imágenes con IA consume **créditos**; tu saldo aparece junto a tu nombre en la barra superior.\n" +
            "- En la pestaña de IA, el botón **Créditos** del pie muestra el coste estimado y tu saldo antes de generar.\n" +
            "- Una vez que la imagen está en la escena, puedes seguir trabajando con ella desde las pestañas **AI Wizard** y **Herramientas** del menú derecho; cada una tiene su propia guía.",
        },
        {
          id: "ai-generate",
          title: "Pestaña Generar con IA",
          videoUrl: null,
          text:
            "Elige un **Estilo**, describe la imagen y haz clic en **Generar Imagen** en el pie de la ventana.\n\n" +
            "- **Escribir Prompt**: describe la imagen tú mismo.\n" +
            "- **Desde Narración**: parte de la narración de la escena; recórtala para dejar solo lo que debe mostrarse y haz clic en **Generar Prompt de Imagen desde Narración** para convertirla en un prompt de imagen.\n" +
            "- **Continuar con Prompt Directo**: envía tu texto tal cual, sin estilo ni textos superpuestos.\n" +
            "- **Enriquecer mi prompt** reescribe lo que has escrito como un prompt más detallado.\n" +
            "- **Estilo**: elige una tarjeta de estilo (**Más estilos** muestra el resto), sube una imagen de **Referencia de Estilo** para copiar su aspecto o elige **Personalizado** y describe tu propio estilo.\n" +
            "- **Mejoras Opcionales**: activa **Texto en Imagen** para colocar hasta cinco textos en la imagen (añade más con **+ Agregar Texto**, cada uno con su propia posición y estilo) y define la **Relación de Aspecto** (16:9, 9:16, 1:1, 4:3) y la **Resolución** (1K, 2K, 4K).\n" +
            "- **Configuración Avanzada**: un **Prompt Negativo** indica lo que la imagen no debe contener.",
        },
        {
          id: "library-upload",
          title: "Biblioteca y subida",
          videoUrl: null,
          text:
            "- **Elegir de biblioteca**: explora o busca las imágenes que ya tienes en tu biblioteca; haz doble clic en una imagen, o pasa el cursor sobre ella y pulsa su botón **+**, para añadirla a la escena.\n" +
            "- **Subir mi imagen**: arrastra y suelta un archivo de imagen o haz clic para buscarlo y, después, haz clic en **Subir Fichero**. La imagen se añade a la escena actual en cuanto termina la subida; **Borrar** te permite elegir otro archivo. Se aceptan imágenes de hasta 100 MB.",
        },
        {
          id: "history",
          title: "Registro de Actividad",
          videoUrl: null,
          text:
            "Haz clic en el icono del reloj del encabezado de la ventana para abrir el **Registro de Actividad**; vuelve a hacer clic para regresar a la pestaña de IA. Muestra las generaciones de imágenes con IA recientes de esta formación con su modelo, fecha, prompt y estado, junto con tu **Saldo Actual** y el **Gasto en Imágenes**.\n\n" +
            "- **Todas las Actividades** amplía la lista a todos los tipos de trabajos de IA.\n" +
            "- Haz doble clic en una fila, o usa su botón **+**, para añadir un resultado anterior a la escena actual **sin volver a gastar créditos**.",
        },
        {
          id: "providers-results",
          title: "Proveedores de IA y resultados",
          videoUrl: null,
          text:
            "- **Proveedor de IA**: **Automático** usa Nano Banana Pro. Cambia a **Personalizado** para elegir uno o varios entre Nano Banana 2, Nano Banana Pro, Grok, Flux y GPT Image; cada proveedor seleccionado crea su propia imagen, así que puedes compararlas una al lado de otra. En ese caso, la estimación de **Créditos** muestra el coste por proveedor.\n" +
            "- La **Resolución** solo se puede cambiar cuando todos los proveedores seleccionados son modelos Nano Banana.\n" +
            "- Cada resultado ofrece **Agregar a Escena** y **Aidea Wizard AI**, que abre las herramientas de edición con IA (editar, quitar el fondo, aumentar la resolución, animar y más) dentro de la propia ventana; **Cambiar Herramienta** vuelve a la lista de herramientas.\n" +
            "- **Regenerar** vuelve a generar con los mismos ajustes. **Cancelar** regresa al formulario y conserva tu prompt y tus opciones.\n" +
            "- Las imágenes generadas también aparecen en la bandeja de trabajos, donde pueden aprobarse más tarde para añadirlas a la escena.",
        },
      ],
    },
  },
};

export default addImageGuide;
